import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MaskLine, Reveal } from "../components/Reveal";
import { ArrowRight } from "lucide-react";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden">
        <motion.div style={{ y: yImg }} className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-mj-red/10 via-mj-bg to-mj-bg" />
          <div className="absolute right-0 top-0 bottom-0 w-full md:w-1/2 bg-mj-red">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-[1px] h-2/3 bg-gradient-to-b from-transparent via-mj-gold to-transparent" />
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 pointer-events-none select-none">
              <img src="/logo.png" alt="Manjaadii" className="w-2/3 md:w-1/2 max-w-md object-contain opacity-95" />
              <span className="font-serif-mj text-mj-bg text-4xl md:text-5xl tracking-[0.35em] pl-[0.35em]">MANJAADII</span>
              <span className="block w-40 md:w-56 h-px bg-mj-gold my-1" />
              <span className="font-serif-mj text-mj-gold text-4xl md:text-5xl tracking-[0.1em] pl-[0.1em]">Wear Your Heritage</span>
            </div>
          </div>
        </motion.div>

        <motion.div style={{ opacity }} className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pb-24 pt-40 w-full">
          <MaskLine delay={0.1}><span className="overline text-mj-red">Rooted in Kerala · Crafted in Bangalore</span></MaskLine>
          <h1 className="font-serif-mj tracking-tight text-mj-text mt-6 text-6xl sm:text-7xl lg:text-8xl leading-[0.92]">
            <MaskLine delay={0.25}>The timeless</MaskLine>
            <MaskLine delay={0.4}><span className="italic text-mj-red">elegance</span> of</MaskLine>
            <MaskLine delay={0.55}>ethnic wear</MaskLine>
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-md mt-8"
          >
            <p className="font-sans-mj text-mj-muted leading-relaxed">
              Beautifully crafted kasavu sarees, set mundu, mul cotton and festive attire, designed for today's generation without losing the essence of heritage.
            </p>
            <Link to="/collection" data-testid="hero-cta" className="group inline-flex items-center gap-3 mt-8 bg-mj-red text-mj-bg px-8 py-4 overline hover:bg-mj-reddark transition-colors">
              Explore the Collection
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="py-8 border-y border-mj-border bg-mj-surface/50">
        <Marquee speed={40} gradient={false}>
          {["wear your heritage", "kasavu traditions", "handloom weaves", "Kerala's rich legacy"].map((t, i) => (
            <span key={i} className="font-serif-mj italic text-4xl md:text-5xl text-mj-text/80 mx-10 flex items-center gap-10">
              {t} <span className="text-mj-gold not-italic text-2xl">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* INTRO */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-40">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <Reveal><span className="overline text-mj-golddeepdeep">At Manjaadii</span></Reveal>
          </div>
          <div className="md:col-span-8">
            <Reveal delay={0.1}>
              <p className="font-serif-mj text-3xl md:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-mj-text">
                We believe tradition is more than clothing, it's a feeling of belonging, a connection to family, and a story passed down through generations.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="font-sans-mj text-mj-muted leading-relaxed mt-10 max-w-2xl">
                Whether you're dressing for a festival, a wedding, a temple visit, or simply embracing your cultural roots, every piece is thoughtfully chosen for its quality, comfort, and craftsmanship, allowing you to celebrate every occasion with confidence and grace.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="bg-mj-surface/40 border-y border-mj-border py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal><h2 className="font-serif-mj text-5xl md:text-6xl tracking-tight text-mj-text mb-16">The Weaves</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-px bg-mj-border border border-mj-border">
            {[
              { n: "01", t: "Kasavu & Tissue", d: "The luminous gold-bordered soul of Kerala." },
              { n: "02", t: "Set Mundu", d: "Two-piece grace, worn on festival mornings." },
              { n: "03", t: "Cotton & Silk", d: "From breezy mul cotton to festive silk." },
            ].map((c, i) => (
              <Reveal key={i} delay={i * 0.1} className="bg-mj-bg p-10 md:p-12 min-h-[280px] flex flex-col justify-between group hover:bg-mj-surface transition-colors">
                <span className="font-serif-mj text-6xl text-mj-gold/40">{c.n}</span>
                <div>
                  <h3 className="font-serif-mj text-3xl text-mj-text mb-2">{c.t}</h3>
                  <p className="font-sans-mj text-sm text-mj-muted">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-14">
            <Link to="/collection" data-testid="view-collection-cta" className="group inline-flex items-center gap-3 overline text-mj-red">
              View the full collection <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* OCCASION */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-40">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="aspect-[4/5] bg-mj-red relative overflow-hidden flex flex-col items-center justify-center gap-3 px-8">
              <img src="/logo.png" alt="Manjaadii" className="w-1/2 max-w-[220px] object-contain opacity-95 select-none pointer-events-none" />
              <span className="font-serif-mj text-mj-bg text-2xl md:text-3xl tracking-[0.3em] pl-[0.3em]">MANJAADII</span>
              <span className="block w-28 h-px bg-mj-gold" />
              <span className="font-serif-mj text-mj-gold text-2xl md:text-3xl tracking-[0.08em] pl-[0.08em]">Wear Your Heritage</span>
              <div className="absolute inset-6 border border-mj-gold/30 pointer-events-none" />
            </div>
          </Reveal>
          <div>
            <Reveal><span className="overline text-mj-golddeep">For every occasion</span></Reveal>
            <Reveal delay={0.1}><h2 className="font-serif-mj text-4xl md:text-6xl tracking-tight text-mj-text mt-5 leading-tight">Make every moment special</h2></Reveal>
            <Reveal delay={0.2}><p className="font-sans-mj text-mj-muted leading-relaxed mt-8">Explore our curated collections and discover ethnic wear that blends authenticity, elegance, and modern style, making Kerala's rich legacy a part of your everyday journey.</p></Reveal>
            <Reveal delay={0.3}><Link to="/story" data-testid="story-cta" className="mj-link-underline overline text-mj-red mt-8 inline-block">Read our story</Link></Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
